// @ts-strict-ignore
import { useCallback } from 'react';
import { useSnackBar } from '@usealloy/component-library';
import type { SnippetVersion } from 'flow/service/snippets';
import { captureExceptionsToSentry } from '~/helpers/exceptionHandler';
import { regeneratePublicIds } from 'flow/service/publicIds/regenerateIds';
import { snippetVersionSchema } from 'flow/service/snippets/schema';
import { ZodError } from 'zod';
import { useAppDispatch, useAppSelector } from '~/store/reduxHooks';
import {
  createSnippetVersion,
  getUpstreamSubgraphFromAction,
  doesSnippetContainIncompatibleServices,
  getInvalidOutcomeNames,
  pruneSnippetVersion,
  canCopyAction,
  getIncompatibleActions,
  scrubSnippetVersion,
} from 'flow/service/snippets/snippets';
import { insertSnippetVersionThunk } from 'flow/store/redux/thunks/snippets';
import { useEditorAccess } from 'flow/editor/context';
import { EditorMode } from 'flow/editor/context/constants';
import {
  selectGlobalServiceConfigOptions,
  selectWorkflowData,
  selectWorkflowIsSnippet,
  selectWorkflowProps,
  selectWorkflowSupportsCopyPaste,
} from 'flow/store/redux/selectors';
import type { Action } from 'flow/store/engine/types';
import { listServiceObjects } from 'flow/service/services';
// BUILD-4687: disabled temporarily
// import { resolveServiceConfigDefaultsIntoActions } from './helpers';


type UseCopyPasteHandlersReturn = {
  handleCopy: () => Promise<void>;
  handlePaste: () => Promise<void>;
};

export function useCopyPasteHandlers(): UseCopyPasteHandlersReturn {
  const isSnippet = useAppSelector(selectWorkflowIsSnippet);
  const workflowData = useAppSelector(selectWorkflowData);
  const workflowProps = useAppSelector(selectWorkflowProps);
  const globalServiceConfigOptions = useAppSelector(selectGlobalServiceConfigOptions);

  const wfSupportsCopyPaste = useAppSelector(selectWorkflowSupportsCopyPaste);

  const addSnackBar = useSnackBar();
  const dispatch = useAppDispatch();

  const { setEditorMode, resetEditorMode } = useEditorAccess();

  const alertUnsupportedCopyPaste = useCallback(() => {
    addSnackBar({
      duration: 5_000,
      type: 'danger',
      title: wfSupportsCopyPaste.reason,
    });
  }, [addSnackBar, wfSupportsCopyPaste.reason]);


  /**
   * Copies workflow info to the clipboard in JSON format.
   * If an actionId is supplied, copies a subgraph of all workflow data leading up to and including that action.
   */
  const handleCopy = useCallback(async (actionId?: Action['publicId']) => {
    if (!wfSupportsCopyPaste.supported) {
      alertUnsupportedCopyPaste();
      return;
    }

    try {
      const data = actionId ? getUpstreamSubgraphFromAction(workflowData, actionId) : workflowData;

      // Validate that all outcome actions have corresponding outcome props
      const invalidOutcomeNames = getInvalidOutcomeNames({
        actions: data.actions,
        outcomes: workflowProps.outcomes,
      });
      if (invalidOutcomeNames.length > 0) {
        addSnackBar({
          duration: 5_000,
          title: `Invalid outcome(s), please replace or remove the following: "${invalidOutcomeNames.join(', ')}"`,
          type: 'danger',
        });
        return;
      }

      // BUILD-4687: `resolveServiceConfigDefaultsIntoActions` temporarily disabled
      // once reenabled createSnippetVersion should be passed actionsWithDefaults instead of data.actions
      // const actionsWithDefaults = resolveServiceConfigDefaultsIntoActions({
      //   actions: data.actions,
      //   services: workflowProps.services,
      // });

      const snippet: SnippetVersion = createSnippetVersion({
        actions: data.actions,
        thresholds: data.thresholds,
        dataRoutes: data.dataRoutes,
        tags: workflowProps.tags,
        outcomes: workflowProps.outcomes,
        services: listServiceObjects(workflowProps.services),
        publishedAttributes: workflowProps.publishedAttributes,
        reasonCodeGroups: workflowProps.reasonCodeGroups,
        lists: workflowProps.lists,
        globalServiceConfigOptions: workflowProps.globalServiceConfigOptions,
        snippets: Object.values(workflowProps.snippets),
      });

      const {
        snippet: scrubbedSnippet,
        impactedServiceActions,

      } = scrubSnippetVersion({ snippet, globalServiceConfigOptions });

      const prunedSnippet = pruneSnippetVersion({
        snippet: scrubbedSnippet,
        actionsToPrune: getIncompatibleActions({ snippet, test: canCopyAction }),
      });

      await navigator.clipboard.writeText(JSON.stringify(prunedSnippet));

      addSnackBar({
        duration: 5_000,
        title: `Successfully copied ${isSnippet ? 'Snippet' : 'Workflow'} to clipboard.`,
        type: 'success',
      });

      if (impactedServiceActions?.length > 0) {
        addSnackBar({
          duration: 5_000,
          title: `The service credentials in the following service nodes are removed in the workflow copied in the clipboard: ${impactedServiceActions.join(', ')}`,
          type: 'success',
        });
      }
    } catch (error) {
      captureExceptionsToSentry(error);
      addSnackBar({
        duration: 2_000,
        title: 'Something went wrong while copying the workflow data.',
        type: 'danger',
      });
    }
  }, [
    addSnackBar,
    alertUnsupportedCopyPaste,
    wfSupportsCopyPaste,
    workflowData,
    workflowProps,
    globalServiceConfigOptions,
    isSnippet,
  ]);

  const handlePaste = useCallback(async () => {
    if (!wfSupportsCopyPaste.supported) {
      alertUnsupportedCopyPaste();
      return;
    }

    const text = await navigator.clipboard.readText();
    let snippet: SnippetVersion;
    try {
      const object = JSON.parse(text);
      // todo BUILD-3979 - finalize the Snippet schema
      /* @ts-expect-error */
      snippet = snippetVersionSchema.parse(object);
    } catch (err) {
      if (err instanceof ZodError || err instanceof SyntaxError) {
        addSnackBar({
          duration: 2_000,
          title: 'Cannot paste invalid workflow data',
          type: 'danger',
        });
      }
    }

    if (snippet) {
      const containsIncompatibleServices = doesSnippetContainIncompatibleServices({
        accountServices: Object.values(workflowProps.services),
        snippet,
      });

      if (containsIncompatibleServices) {
        addSnackBar({
          duration: 5_000,
          title: 'Paste failed: incompatible services for this Workflow type',
          type: 'danger',
        });
        // block paste
        return;
      }


      try {
        setEditorMode(EditorMode.ReadOnly);
        const remappedSnippet = regeneratePublicIds(snippet);
        await dispatch(insertSnippetVersionThunk({ snippet: remappedSnippet }));
        addSnackBar({
          duration: 2_000,
          title: 'Successfully pasted workflow from clipboard.',
          type: 'success',
        });
      } catch (error) {
        resetEditorMode();
        addSnackBar({
          duration: 2_000,
          title: 'Failed to paste workflow. Please try again.',
          type: 'danger',
        });
      }
    }
  }, [
    addSnackBar,
    dispatch,
    resetEditorMode,
    setEditorMode,
    alertUnsupportedCopyPaste,
    wfSupportsCopyPaste,
    workflowProps,
  ]);

  return {
    handleCopy,
    handlePaste,
  };
}
